import React, { useEffect, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { ListChecks, Check, X, Clock, Loader2 } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import { useUzkaObrazovka } from "../lib/obrazovka.js";

// Dialog při zastavení měření času s navázaným úkolem (17.7.2026).
// Sdílený mezi stránkou Time report a stopkami v horní liště (TimerWidget).
//
// Úkol nejde označit hotový, dokud nejsou odškrtnuté všechny podúkoly —
// proto dialog checklist rovnou zobrazí a podúkoly jdou odškrtnout tady,
// bez odbíhání na board. "Hotovo" se odemkne až po odškrtnutí všech.
//
// 8.9.2026 — tři opravy:
//  1) Vykreslení přes createPortal do document.body. Horní lišta má backdrop-blur
//     a prvek s rozostřením je pro potomky s position:fixed rámem, takže se dialog
//     lepil na lištu, vylezl z ní, ořízl se a na část podúkolů nešlo kliknout.
//  2) Na telefonu přes celý displej (hlavička, rolující seznam, tlačítka dole),
//     na počítači vystředěné okno 480 px. Dřív se dlouhý obsah nevešel.
//  3) V seznamu jsou nesplněné podúkoly nahoře a hotové dole pod nadpisem
//     "Hotové". Řadí se JEN zobrazení, uložené pořadí na nástěnce se nemění.
export default function StopUkolDialog({ open, ukolId, ukolNazev, busy, onDone, onNotDone, onClose }) {
  const uzka = useUzkaObrazovka();
  const [podukoly, setPodukoly] = useState(null); // null = načítám
  const [togglingId, setTogglingId] = useState(null);

  const load = useCallback(async () => {
    if (!ukolId) { setPodukoly([]); return; }
    try {
      const r = await apiFetch(`/api/operations-ukoly?taskId=${ukolId}`);
      const j = await r.json();
      setPodukoly(r.ok ? (j.podukoly || []) : []);
    } catch { setPodukoly([]); }
  }, [ukolId]);
  useEffect(() => { if (open) { setPodukoly(null); load(); } }, [open, load]);

  const zbyvajici = useMemo(() => (podukoly || []).filter((p) => !p.hotovo), [podukoly]);
  const hotove = useMemo(() => (podukoly || []).filter((p) => p.hotovo), [podukoly]);

  if (!open) return null;
  const zbyva = zbyvajici.length;
  const hotovoLze = podukoly !== null && zbyva === 0;

  const toggle = async (p) => {
    setTogglingId(p.id);
    try {
      const r = await apiFetch("/api/operations-ukoly", { method: "POST", body: JSON.stringify({ action: "toggleSubtask", subtaskId: p.id }) });
      if (r.ok) setPodukoly((list) => (list || []).map((x) => (x.id === p.id ? { ...x, hotovo: !x.hotovo } : x)));
    } catch { /* necháme jak je */ }
    finally { setTogglingId(null); }
  };

  // Řádek podúkolu — klikací je celý, ne jen zaškrtávátko (na telefonu 48 px).
  const Radek = ({ p }) => (
    <label className={`flex items-center gap-3 px-3 cursor-pointer ${uzka ? "min-h-[48px] text-[15px]" : "min-h-[44px] text-[15px]"}`}>
      <input type="checkbox" checked={!!p.hotovo} disabled={togglingId === p.id} onChange={() => toggle(p)}
        className={`shrink-0 accent-brand ${uzka ? "h-6 w-6" : "h-5 w-5"}`} />
      <span className={`min-w-0 flex-1 ${p.hotovo ? "text-slate-400 line-through" : "text-slate-800"}`}>{p.text}</span>
      {togglingId === p.id && <Loader2 className="w-4 h-4 shrink-0 animate-spin text-slate-400" />}
    </label>
  );

  const Seznam = () => {
    if (podukoly === null) {
      return <div className="flex items-center gap-2 py-2 text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Načítám podúkoly…</div>;
    }
    if (podukoly.length === 0) return null;
    return (
      <>
        {zbyvajici.length > 0 && (
          <>
            <div className="mt-4 flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Zbývá</span>
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700">{zbyvajici.length}</span>
            </div>
            <div className="mt-1 divide-y divide-slate-100 rounded-xl border border-slate-200">
              {zbyvajici.map((p) => <Radek key={p.id} p={p} />)}
            </div>
          </>
        )}
        {hotove.length > 0 && (
          <>
            <div className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Hotové</div>
            <div className="mt-1 divide-y divide-slate-100 rounded-xl border border-slate-200 bg-slate-50">
              {hotove.map((p) => <Radek key={p.id} p={p} />)}
            </div>
          </>
        )}
      </>
    );
  };

  const Tlacitka = () => (
    <div className="space-y-2">
      <button onClick={onDone} disabled={busy || !hotovoLze}
        title={hotovoLze ? "" : `Nejdřív odškrtni podúkoly (zbývá ${zbyva})`}
        className="btn btn-primary w-full">
        <Check className="w-4 h-4" />
        {hotovoLze ? "Hotovo, zastavit měření" : `Hotovo (zbývá${zbyva > 1 && zbyva < 5 ? "jí" : ""} ${zbyva} podúkol${zbyva >= 5 ? "ů" : zbyva > 1 ? "y" : ""})`}
      </button>
      <button onClick={onNotDone} disabled={busy}
        className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-slate-800 px-4 text-[15px] font-semibold text-white hover:bg-slate-700">
        <X className="w-4 h-4" /> Zastavit, není hotový
      </button>
      <button onClick={onClose}
        className="btn btn-secondary w-full">
        <Clock className="w-4 h-4" /> Pokračovat v měření
      </button>
    </div>
  );

  // Telefon: přes celý displej, tlačítka drží dole nad spodní lištou.
  if (uzka) {
    return createPortal(
      <div className="fixed inset-0 z-[70] flex flex-col bg-white">
        <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
          <ListChecks className="w-5 h-5 shrink-0 text-brand-deep" />
          <span className="min-w-0 flex-1 font-bold text-slate-900">Je úkol hotový?</span>
          <button onClick={onClose} aria-label="Pokračovat v měření" title="Pokračovat v měření"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          <div className="text-[15px] font-bold text-slate-900">{ukolNazev}</div>
          <div className="mt-1 text-[13px] text-slate-500">Pokud hotový není, vrátí se z Procesu zpět do původního sloupce (např. Týdenní).</div>
          <Seznam />
        </div>
        <div className="safe-bottom border-t border-slate-200 px-4 py-3">
          <Tlacitka />
        </div>
      </div>,
      document.body
    );
  }

  // Počítač: vystředěné okno 480 px, obsah roluje uvnitř.
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-brand-dark/50 p-4 backdrop-blur-sm pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-[480px] flex-col rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-6 pt-6">
          <ListChecks className="w-6 h-6 shrink-0 text-brand-deep" />
          <span className="min-w-0 flex-1 text-lg font-bold text-slate-900">Je úkol hotový?</span>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-3">
          <div className="text-[15px] font-bold text-slate-900">{ukolNazev}</div>
          <div className="mt-1 text-sm text-slate-500">Pokud hotový není, vrátí se z Procesu zpět do původního sloupce (např. Týdenní).</div>
          <Seznam />
        </div>
        <div className="px-6 pb-6 pt-5">
          <Tlacitka />
        </div>
      </div>
    </div>,
    document.body
  );
}
